/**
 * CJSUSPENSION brand service hub enhancements.
 * Keeps the brand hubs concise once the complete clickable model directory
 * has loaded, while preserving useful service, upgrade, booking and support guidance.
 */
(function () {
  'use strict';

  var currentPath = String(window.location.pathname || '').toLowerCase();
  var FOX_HUB_PATH = '/pages/fox-suspension-service';
  var FOX_HERO_PART_COUNT = 9;

  var brandPages = {
    '/pages/fox-suspension-service': { brand: 'Fox', product: 'suspension' },
    '/pages/rockshox-suspension-service': { brand: 'RockShox', product: 'suspension' },
    '/pages/ohlins-suspension-service': { brand: 'Öhlins', product: 'suspension' },
    '/pages/marzocchi-suspension-service': { brand: 'Marzocchi', product: 'suspension' },
    '/pages/dvo-suspension-service': { brand: 'DVO', product: 'suspension' },
    '/pages/cane-creek-suspension-service': { brand: 'Cane Creek', product: 'suspension' },
    '/pages/vorsprung-suspension-service': { brand: 'Vorsprung', product: 'suspension' },
    '/pages/ext-suspension-service': { brand: 'EXT', product: 'suspension' },
    '/pages/bos-suspension-service': { brand: 'BOS', product: 'suspension' },
    '/pages/x-fusion-suspension-service': { brand: 'X-Fusion', product: 'suspension' },
    '/pages/fast-suspension-service': { brand: 'FAST', product: 'suspension' },
    '/pages/manitou-suspension-service': { brand: 'Manitou', product: 'suspension' },
    '/pages/ks-dropper-service': { brand: 'KS', product: 'dropper post' },
    '/pages/specialized-dropper-service': { brand: 'Specialized', product: 'dropper post' }
  };

  /*
   * Brand-level upgrade summaries are intentionally conservative.
   * They reflect confirmed model mappings in the CJSUSPENSION registry and
   * send customers to the exact model page or a confirmed CJS product/service.
   * Exact fitment remains model/generation specific.
   */
  var performanceUpgrades = {
    '/pages/fox-suspension-service': [
      {
        label: 'Vorsprung fork upgrades',
        title: 'Smashpot, Secus, Luftkappe & Fractive',
        text: 'Selected Fox 32, 34, 36, 38 and 40 applications have confirmed Vorsprung upgrade support. The exact upgrade depends on the fork generation, air spring and damper.',
        models: 'Selected Fox 32 / 34 / 36 / 38 / 40',
        link: '#brand-products',
        linkLabel: 'Check Compatible Fox Models'
      },
      {
        label: 'Fluid Focus fork tuning',
        title: 'Fork Damper Performance Upgrades',
        text: 'Confirmed Fluid Focus piston and re-valve options are available for selected Fox 34, 36, 38, 40 and Podium damper configurations.',
        models: 'Fox 34 / 36 / 38 / 40 / Podium',
        link: '/products/fluid-focus-fox-fork-damper-high-flow-piston-re-valve-kits',
        linkLabel: 'View Fox Damper Upgrades'
      },
      {
        label: 'Rear shock performance',
        title: 'Fluid Focus, Bump Stops & Coil Springs',
        text: 'Selected Float and DHX-family shocks support confirmed Fluid Focus tuning, bump-stop, split-preload and spring options. Exact fitment is shown on each model page.',
        models: 'Selected Float / Float X / Float X2 / DHX / DHX2 / legacy families',
        link: '/collections/rear-shock-springs',
        linkLabel: 'View Rear Shock Springs'
      },
      {
        label: 'Fox upgrade',
        title: 'Float X / DHX LSC Adjuster Upgrade',
        text: 'Low-speed compression adjuster upgrade for confirmed 2022+ Fox Float X and DHX Performance Series applications.',
        models: '2022+ Float X / DHX Performance Series',
        link: '/products/fox-upgrade-lsc-adjuster-2022-float-x-and-dhx',
        linkLabel: 'View LSC Adjuster Upgrade'
      }
    ],
    '/pages/rockshox-suspension-service': [
      {
        label: 'Vorsprung fork upgrades',
        title: 'Smashpot, Secus & Luftkappe',
        text: 'Confirmed Vorsprung upgrade support covers selected Pike, Lyrik, ZEB, BoXXer, Domain and Yari applications. Exact compatibility differs by model and generation.',
        models: 'Pike / Lyrik / ZEB / BoXXer / Domain / Yari',
        link: '#brand-products',
        linkLabel: 'Check Compatible RockShox Models'
      },
      {
        label: 'Fluid Focus fork tuning',
        title: 'Charger 3 / 3.1 / 3.2 Upgrades',
        text: 'Piston and re-valve upgrades are confirmed for selected Charger 3 and 3.1 applications. Charger 3.2 is currently confirmed for ZEB and BoXXer only.',
        models: 'Pike / Lyrik / ZEB / BoXXer, generation dependent',
        link: '/products/tuning-kits',
        linkLabel: 'View RockShox Tuning Kits'
      },
      {
        label: 'Vorsprung rear shock upgrade',
        title: 'Tractive Tuning Upgrade',
        text: 'Confirmed for compatible Super Deluxe Air and Coil RC3 / RCT / RTR / RC variants and Monarch Plus RC3. Exact shock generation and controls are checked first.',
        models: 'Super Deluxe Air / Super Deluxe Coil / Monarch Plus RC3',
        link: '/products/tractive-upgrade-kit',
        linkLabel: 'View Tractive Upgrade'
      },
      {
        label: 'Rear shock performance',
        title: 'Vivid, Bump Stop, Preload & Spring Upgrades',
        text: 'Selected Deluxe, Super Deluxe, Vivid, Monarch and Kage families have confirmed Fluid Focus, bump-stop, split-preload or coil-spring upgrade options.',
        models: 'Selected RockShox air and coil rear shocks',
        link: '/collections/suspension-upgrades-tuning/products/bump-stops',
        linkLabel: 'View Bump Stop Upgrades'
      },
      {
        label: 'RockShox Brain 2.0',
        title: 'Bladder to Floating Piston Upgrade',
        text: 'A dedicated bladder-to-IFP conversion is supported on compatible RockShox Brain 2.0 shocks after the exact generation is confirmed.',
        models: 'Compatible RockShox Brain 2.0 only',
        link: '/products/rockshox-brain-2-0-bladder-to-floating-piston',
        linkLabel: 'View Brain 2.0 Upgrade'
      }
    ],
    '/pages/ohlins-suspension-service': [
      {
        label: 'Vorsprung fork upgrade',
        title: 'Smashpot Coil Conversion',
        text: 'Smashpot support is confirmed for selected RXF36, RXF38 and DH38 applications. Fork generation, travel and kit fitment are checked before installation.',
        models: 'RXF36 / RXF38 / DH38',
        link: '#brand-products',
        linkLabel: 'Check Compatible Öhlins Models'
      },
      {
        label: 'Air spring tuning',
        title: 'Rear Shock Volume Setup',
        text: 'Confirmed air-volume tuning is available on selected TXC1Air, TXC2Air, TTX1Air, TTX2Air and compatible high-volume STX22 Air shocks.',
        models: 'TXC1Air / TXC2Air / TTX1Air / TTX2Air / STX22 Air HV',
        link: '/pages/book-a-suspension-service',
        linkLabel: 'Book Performance Setup'
      },
      {
        label: 'Rear shock performance',
        title: 'Bump Stop & Coil Spring Upgrades',
        text: 'Confirmed Fluid Focus bump-stop support is available on selected TTX air and TTX22M shocks, with SAR and Sprindex spring options on compatible TTX22M coil shocks.',
        models: 'TTX1Air / TTX2Air / TTX22M',
        link: '/collections/rear-shock-springs',
        linkLabel: 'View Rear Shock Springs'
      }
    ],
    '/pages/marzocchi-suspension-service': [
      {
        label: 'Vorsprung fork upgrades',
        title: 'Smashpot & Secus',
        text: 'Smashpot is confirmed for selected air-sprung Bomber Z1, Bomber 58 and Super Z applications. Secus support is confirmed on compatible Bomber 58 forks.',
        models: 'Bomber Z1 air / Bomber 58 / Super Z',
        link: '#brand-products',
        linkLabel: 'Check Compatible Marzocchi Models'
      },
      {
        label: 'Fork performance',
        title: 'Damper & Travel Upgrades',
        text: 'Confirmed complete damper and air-shaft travel changes are available on selected Z1, Z2, Bomber 58, Bomber DJ, Super Z and Bomber Z generations.',
        models: 'Selected Marzocchi fork families',
        link: '/pages/book-a-suspension-service',
        linkLabel: 'Book Fork Upgrade Work'
      },
      {
        label: 'Rear shock performance',
        title: 'Bomber CR Piston, Bump Stop & Spring Options',
        text: 'The Bomber CR has a confirmed Fluid Focus piston-kit option plus supported bump-stop and coil-spring upgrades. Selected Bomber Air shocks also support bump-stop and air-volume setup.',
        models: 'Bomber CR / selected Bomber Air applications',
        link: '/products/fluid-focus-marzocchi-rear-shock-piston-kits',
        linkLabel: 'View Bomber CR Piston Kit'
      }
    ],
    '/pages/dvo-suspension-service': [
      {
        label: 'Vorsprung fork upgrade',
        title: 'Smashpot Coil Conversion',
        text: 'Smashpot support is confirmed on compatible DVO Onyx 38 and Onyx SC applications. Exact generation and travel are checked before installation.',
        models: 'Onyx 38 / Onyx SC',
        link: '#brand-products',
        linkLabel: 'Check Compatible DVO Models'
      },
      {
        label: 'Fork performance',
        title: 'Air-Shaft Travel Changes',
        text: 'Confirmed travel-change support covers selected Onyx, Diamond, Sapphire, Emerald and Beryl fork generations using the correct air-shaft configuration.',
        models: 'Selected DVO fork families',
        link: '/pages/book-a-suspension-service',
        linkLabel: 'Book Fork Setup'
      },
      {
        label: 'Rear shock performance',
        title: 'Volume, Bump Stop & Coil Spring Tuning',
        text: 'Topaz-family and Opal air-spring tuning is supported, with confirmed Fluid Focus bump stops on selected Topaz, Jade and Jade X shocks and spring upgrades on compatible Jade-family coil shocks.',
        models: 'Topaz / Topaz Gen 3 / Opal / Jade / Jade X',
        link: '/collections/suspension-upgrades-tuning/products/bump-stops',
        linkLabel: 'View Bump Stop Upgrades'
      }
    ],
    '/pages/cane-creek-suspension-service': [
      {
        label: 'Air spring tuning',
        title: 'Cane Creek Air Volume Setup',
        text: 'Air-volume tuning is confirmed on compatible Air IL / DBair IL / DBInline, DBair / DBair CS and Kitsuma Air families.',
        models: 'Air IL / DBair / Kitsuma Air families',
        link: '/products/cane-creek-air-volume-spacers',
        linkLabel: 'View Cane Creek Volume Spacers'
      },
      {
        label: 'Fluid Focus rear shock upgrade',
        title: 'Bump Stop Upgrades',
        text: 'Confirmed Fluid Focus bump-stop support covers selected Cane Creek air and coil rear-shock families including IL, DB and Kitsuma platforms.',
        models: 'Selected Air IL / Coil IL / DBair / DBcoil / Kitsuma families',
        link: '/collections/suspension-upgrades-tuning/products/bump-stops',
        linkLabel: 'View Bump Stop Upgrades'
      },
      {
        label: 'Coil spring upgrade',
        title: 'SAR & Sprindex Spring Options',
        text: 'SAR fixed-rate and Sprindex adjustable-rate spring options are confirmed on compatible Coil IL, DBcoil and Kitsuma Coil applications.',
        models: 'Coil IL / DBcoil / Kitsuma Coil',
        link: '/collections/rear-shock-springs',
        linkLabel: 'View Rear Shock Springs'
      }
    ],
    '/pages/ext-suspension-service': [
      {
        label: 'Vorsprung fork upgrade',
        title: 'Smashpot for EXT ERA 36',
        text: 'Smashpot V2 support is confirmed for compatible EXT ERA / ERA V2.1 36mm applications. It must not be assumed for ERA LT or Vaia.',
        models: 'Compatible EXT ERA 36 only',
        link: '/pages/book-a-suspension-service',
        linkLabel: 'Book Smashpot Installation'
      }
    ],
    '/pages/manitou-suspension-service': [
      {
        label: 'Vorsprung fork upgrade',
        title: 'Smashpot for Mezzer 3',
        text: 'Smashpot V2 support is confirmed for compatible Manitou M3ZZ3R / Mezzer 3 applications. Earlier Mezzer generations are not assumed compatible.',
        models: 'M3ZZ3R / Mezzer 3 only',
        link: '/pages/book-a-suspension-service',
        linkLabel: 'Book Smashpot Installation'
      }
    ],
    '/pages/fast-suspension-service': [
      {
        label: 'FAST fork upgrades',
        title: '3 Way, UP & DC3 Upgrade Kits',
        text: 'CJSUSPENSION supports confirmed FAST host-fork upgrade families including the 3 Way Kit, UP Kit and DC3 Compression Kit. Host fork and exact kit compatibility are checked before work.',
        models: '3 Way Kit / UP Kit / DC3 Compression Kit',
        link: '/pages/book-a-suspension-service',
        linkLabel: 'Book FAST Upgrade Work'
      }
    ]
  };

  function getAssetBase() {
    var scripts = document.getElementsByTagName('script');

    for (var i = 0; i < scripts.length; i += 1) {
      var src = String(scripts[i].src || '');
      var marker = 'cjs-fox-service-models.js';
      var index = src.indexOf(marker);

      if (index !== -1) return src.slice(0, index);
    }

    return '';
  }

  function loadScript(src) {
    return new Promise(function (resolve, reject) {
      var script = document.createElement('script');
      script.src = src;
      script.async = false;
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }

  function applyFoxHeroImage() {
    if (currentPath !== FOX_HUB_PATH) return;

    var parts = window.CJS_BRANDS_HERO_PARTS || [];
    if (parts.length !== FOX_HERO_PART_COUNT) return;

    var hero = document.querySelector('.cjs-brand-page__hero');
    if (!hero) return;

    var media = hero.querySelector('.cjs-brand-page__hero-media');

    if (!media) {
      media = document.createElement('div');
      media.className = 'cjs-brand-page__hero-media';
      hero.insertBefore(media, hero.firstChild);
    }

    while (media.firstChild) media.removeChild(media.firstChild);

    media.setAttribute('aria-hidden', 'true');
    media.style.backgroundImage = 'url("data:image/webp;base64,' + parts.join('') + '")';
    media.style.backgroundSize = 'cover';
    media.style.backgroundPosition = 'center center';
    media.style.backgroundRepeat = 'no-repeat';
    hero.setAttribute('data-cjs-fox-hero-ready', '');

    window.CJS_BRANDS_HERO_PARTS = null;
  }

  function loadFoxHeroImage() {
    if (currentPath !== FOX_HUB_PATH) return;
    if (window.CJS_FOX_HERO_LOADING) return;

    var hero = document.querySelector('.cjs-brand-page__hero');
    if (hero && hero.hasAttribute('data-cjs-fox-hero-ready')) return;

    var base = getAssetBase();
    if (!base) return;

    window.CJS_FOX_HERO_LOADING = true;
    window.CJS_BRANDS_HERO_PARTS = [];

    var chain = Promise.resolve();

    for (var i = 1; i <= FOX_HERO_PART_COUNT; i += 1) {
      (function (partNumber) {
        chain = chain.then(function () {
          var number = partNumber < 10 ? '0' + partNumber : String(partNumber);
          return loadScript(base + 'cjs-brands-hero-part-' + number + '.js');
        });
      })(i);
    }

    chain.then(function () {
      applyFoxHeroImage();
      window.CJS_FOX_HERO_LOADING = false;
    }).catch(function () {
      window.CJS_FOX_HERO_LOADING = false;
    });
  }

  function styleFoxHeroLogo() {
    if (currentPath !== FOX_HUB_PATH) return;

    var logoWrap = document.querySelector('.cjs-brand-page__hero-logo-wrap');
    if (!logoWrap) return;

    logoWrap.style.background = '#000000';
    logoWrap.style.borderColor = 'rgba(255,255,255,0.18)';
    logoWrap.style.boxShadow = '0 24px 60px rgba(0,0,0,0.30)';
  }

  function buildFaqItem() {
    var item = document.createElement('details');
    item.className = 'cjs-brand-page__faq-item';

    var summary = document.createElement('summary');
    var question = document.createElement('span');
    var icon = document.createElement('span');
    icon.className = 'cjs-brand-page__faq-icon';
    icon.setAttribute('aria-hidden', 'true');

    summary.appendChild(question);
    summary.appendChild(icon);

    var answer = document.createElement('div');
    answer.className = 'cjs-brand-page__faq-answer rte';

    item.appendChild(summary);
    item.appendChild(answer);
    return item;
  }

  function faqCopy(config) {
    return [
      {
        question: 'Which ' + config.brand + ' service should I book?',
        answer: 'Use the service options on this page and the dedicated model page for your exact product. If you are unsure which service interval you need, use our <a href="/pages/suspension-hour-meter">Suspension Hour Meter</a>.'
      },
      {
        question: 'How do I send my ' + config.brand + ' ' + config.product + ' to you?',
        answer: 'Use our <a href="/pages/book-a-suspension-service">Book a Suspension Service</a> page for booking, Queenstown workshop drop-off and tracked courier options.'
      }
    ];
  }

  function tidyFaq() {
    var config = brandPages[currentPath];
    if (!config) return;

    var faq = document.querySelector('.cjs-brand-page__faq');
    var list = faq ? faq.querySelector('.cjs-brand-page__faq-list') : null;
    if (!faq || !list || list.hasAttribute('data-cjs-brand-faq-ready')) return;

    var eyebrow = faq.querySelector('.cjs-brand-page__section-eyebrow');
    if (eyebrow) eyebrow.textContent = 'Quick help';

    var heading = faq.querySelector('.cjs-brand-page__section-header h2');
    if (heading) heading.textContent = 'Common Questions';

    var intro = faq.querySelector('.cjs-brand-page__section-copy');
    if (intro) intro.remove();

    var copy = faqCopy(config);
    var items = Array.prototype.slice.call(list.querySelectorAll('.cjs-brand-page__faq-item'));

    while (items.length < copy.length) {
      var newItem = buildFaqItem();
      list.appendChild(newItem);
      items.push(newItem);
    }

    while (items.length > copy.length) {
      var removed = items.pop();
      removed.remove();
    }

    items.forEach(function (item, index) {
      var question = item.querySelector('summary > span:first-child');
      var answer = item.querySelector('.cjs-brand-page__faq-answer');

      if (question) question.textContent = copy[index].question;
      if (answer) answer.innerHTML = '<p>' + copy[index].answer + '</p>';

      item.open = false;
      item.addEventListener('toggle', function () {
        if (!item.open) return;
        items.forEach(function (other) {
          if (other !== item) other.open = false;
        });
      });
    });

    list.setAttribute('data-cjs-brand-faq-ready', '');
  }

  function simplifyInformation() {
    var config = brandPages[currentPath];
    if (!config) return;

    var information = document.querySelector('.cjs-brand-page__information');
    if (!information || information.hasAttribute('data-cjs-compact-info-ready')) return;

    var cards = Array.prototype.slice.call(information.querySelectorAll('.cjs-brand-page__information-card'));
    if (!cards.length) return;

    var card = cards[0];
    cards.slice(1).forEach(function (extraCard) {
      extraCard.remove();
    });

    information.setAttribute('data-cjs-compact-info-ready', '');
    information.classList.add('cjs-brand-info-compact');
    card.classList.add('cjs-brand-info-compact__card');

    var columns = card.children;
    var left = columns[0];
    var right = columns[1];

    if (left) {
      var eyebrow = left.querySelector('.cjs-brand-page__section-eyebrow');
      if (!eyebrow) {
        eyebrow = document.createElement('p');
        eyebrow.className = 'cjs-brand-page__section-eyebrow';
        left.insertBefore(eyebrow, left.firstChild);
      }
      eyebrow.textContent = 'Before booking';

      var heading = left.querySelector('h2');
      if (!heading) {
        heading = document.createElement('h2');
        left.appendChild(heading);
      }
      heading.textContent = 'Not Sure Which Model You Have?';
    }

    if (right) {
      var text = right.querySelector('.cjs-brand-page__information-text');
      if (!text) {
        text = document.createElement('div');
        text.className = 'cjs-brand-page__information-text rte';
        right.insertBefore(text, right.firstChild);
      }

      text.innerHTML = '<p>Send us the exact model, production year and a clear photo of the product markings. We will confirm support before you send it in.</p>';

      Array.prototype.slice.call(right.querySelectorAll('a')).forEach(function (link) {
        link.remove();
      });

      var actions = document.createElement('div');
      actions.className = 'cjs-brand-compact-actions';

      var contact = document.createElement('a');
      contact.className = 'cjs-brand-page__button cjs-brand-page__button--primary';
      contact.href = '/pages/contact';
      contact.textContent = 'Contact CJSuspension';

      var book = document.createElement('a');
      book.className = 'cjs-brand-page__button cjs-brand-compact-book';
      book.href = '/pages/book-a-suspension-service';
      book.textContent = 'Book a Service';

      actions.appendChild(contact);
      actions.appendChild(book);
      right.appendChild(actions);
    }
  }

  function simplifyBrandPage() {
    if (!brandPages[currentPath]) return;

    var directory = document.querySelector('[data-cjs-brand-model-directory]');
    if (!directory) return;

    var products = document.querySelector('.cjs-brand-page__products');
    if (products) products.remove();

    document.querySelectorAll('[data-cjs-extra-fox-models]').forEach(function (card) {
      card.remove();
    });
  }

  function tidyServicesHeading() {
    var config = brandPages[currentPath];
    var services = document.querySelector('.cjs-brand-page__services');
    if (!config || !services) return;

    var eyebrow = services.querySelector('.cjs-brand-page__section-eyebrow');
    if (eyebrow) eyebrow.textContent = 'CJSUSPENSION workshop services';

    var heading = services.querySelector('.cjs-brand-page__section-header h2');
    if (heading) heading.textContent = config.brand + ' Workshop Services';
  }

  function injectPerformanceUpgrades() {
    var upgrades = performanceUpgrades[currentPath];
    if (!upgrades || !upgrades.length) return;
    if (document.querySelector('[data-cjs-brand-performance-upgrades]')) return;

    var services = document.querySelector('.cjs-brand-page__services');
    if (!services || !services.parentNode) return;

    var section = document.createElement('section');
    section.className = 'cjs-brand-performance-upgrades';
    section.setAttribute('data-cjs-brand-performance-upgrades', '');

    var header = document.createElement('div');
    header.className = 'cjs-brand-performance-upgrades__header';

    var titleWrap = document.createElement('div');
    var eyebrow = document.createElement('p');
    eyebrow.className = 'cjs-brand-page__section-eyebrow';
    eyebrow.textContent = 'Performance upgrades';

    var heading = document.createElement('h2');
    heading.textContent = brandPages[currentPath].brand + ' Performance Upgrades';

    titleWrap.appendChild(eyebrow);
    titleWrap.appendChild(heading);

    var intro = document.createElement('p');
    intro.className = 'cjs-brand-performance-upgrades__intro';
    intro.textContent = 'Only confirmed CJSUSPENSION upgrade families are shown here. Select your exact model above for model-specific compatibility, service requirements and fitment notes.';

    header.appendChild(titleWrap);
    header.appendChild(intro);
    section.appendChild(header);

    var grid = document.createElement('div');
    grid.className = 'cjs-brand-performance-upgrades__grid';

    upgrades.forEach(function (upgrade) {
      var card = document.createElement('article');
      card.className = 'cjs-brand-performance-upgrades__card';

      var label = document.createElement('p');
      label.className = 'cjs-brand-performance-upgrades__label';
      label.textContent = upgrade.label;

      var cardTitle = document.createElement('h3');
      cardTitle.textContent = upgrade.title;

      var text = document.createElement('p');
      text.className = 'cjs-brand-performance-upgrades__text';
      text.textContent = upgrade.text;

      card.appendChild(label);
      card.appendChild(cardTitle);
      card.appendChild(text);

      if (upgrade.models) {
        var models = document.createElement('p');
        models.className = 'cjs-brand-performance-upgrades__models';
        models.innerHTML = '<strong>Confirmed range:</strong> ' + upgrade.models;
        card.appendChild(models);
      }

      if (upgrade.link && upgrade.linkLabel) {
        var link = document.createElement('a');
        link.className = 'cjs-brand-page__card-link';
        link.href = upgrade.link;
        link.innerHTML = '<span>' + upgrade.linkLabel + '</span><span aria-hidden="true">→</span>';
        card.appendChild(link);
      }

      grid.appendChild(card);
    });

    section.appendChild(grid);
    services.insertAdjacentElement('afterend', section);
  }

  function addStyles() {
    if (document.getElementById('cjs-brand-service-hub-styles')) return;

    var style = document.createElement('style');
    style.id = 'cjs-brand-service-hub-styles';
    style.textContent =
      '.cjs-brand-page__trust-item{gap:17px;}' +
      '.cjs-brand-page__trust-icon{width:46px!important;min-width:46px!important;height:46px!important;border-radius:50%!important;color:var(--cjs-accent)!important;background:#ffffff!important;border:1px solid rgba(168,50,58,.24)!important;box-shadow:0 0 0 5px #f7f5f2,0 6px 14px rgba(17,17,17,.08)!important;transition:transform 160ms ease,box-shadow 160ms ease;}' +
      '.cjs-brand-page__trust-icon svg{width:22px!important;height:22px!important;}' +
      '.cjs-brand-page__trust-item:hover .cjs-brand-page__trust-icon{transform:translateY(-1px);box-shadow:0 0 0 5px #f7f5f2,0 9px 20px rgba(17,17,17,.11)!important;}' +
      '.cjs-brand-page__trust-item strong{font-size:15px;letter-spacing:-.01em;}' +
      '.cjs-brand-page__trust-item div>span{margin-top:5px;line-height:1.45;}' +
      '.cjs-brand-performance-upgrades{margin-top:64px;padding-top:58px;border-top:1px solid var(--cjs-border,#e6e2dd);}' +
      '.cjs-brand-performance-upgrades__header{display:grid;grid-template-columns:minmax(0,1fr) minmax(280px,.8fr);gap:34px;align-items:end;margin-bottom:26px;}' +
      '.cjs-brand-performance-upgrades__header h2{margin:0;color:var(--cjs-text,#111);font-size:clamp(31px,4vw,48px);font-weight:800;line-height:1.02;letter-spacing:-.035em;}' +
      '.cjs-brand-performance-upgrades__intro{margin:0;color:var(--cjs-muted,#606060);font-size:14px;line-height:1.65;}' +
      '.cjs-brand-performance-upgrades__grid{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:16px;}' +
      '.cjs-brand-performance-upgrades__card{display:flex;flex-direction:column;min-height:100%;padding:24px;border:1px solid var(--cjs-border,#e6e2dd);border-radius:var(--cjs-radius,10px);background:var(--cjs-card-background,#fff);}' +
      '.cjs-brand-performance-upgrades__label{margin:0 0 10px;color:var(--cjs-accent,#a8323a);font-size:10px;font-weight:800;letter-spacing:.12em;text-transform:uppercase;}' +
      '.cjs-brand-performance-upgrades__card h3{margin:0 0 11px;color:var(--cjs-text,#111);font-size:21px;font-weight:800;line-height:1.15;letter-spacing:-.02em;}' +
      '.cjs-brand-performance-upgrades__text{margin:0;color:var(--cjs-muted,#606060);font-size:13px;line-height:1.6;}' +
      '.cjs-brand-performance-upgrades__models{margin:16px 0 0;padding-top:14px;border-top:1px solid var(--cjs-border,#e6e2dd);color:var(--cjs-muted,#606060);font-size:12px;line-height:1.5;}' +
      '.cjs-brand-performance-upgrades__models strong{color:var(--cjs-text,#111);}' +
      '.cjs-brand-performance-upgrades__card .cjs-brand-page__card-link{margin-top:auto;padding-top:18px;}' +
      '.cjs-brand-info-compact{margin-top:48px!important;}' +
      '.cjs-brand-info-compact__card{padding:30px!important;align-items:center!important;}' +
      '.cjs-brand-info-compact__card h2{max-width:430px;font-size:clamp(28px,3vw,40px)!important;line-height:1.02!important;}' +
      '.cjs-brand-info-compact__card .cjs-brand-page__information-text{max-width:620px;margin-bottom:18px!important;}' +
      '.cjs-brand-compact-actions{display:flex;flex-wrap:wrap;gap:10px;align-items:center;}' +
      '.cjs-brand-compact-book{display:inline-flex;align-items:center;justify-content:center;border:1px solid var(--cjs-accent,#a8323a);background:#fff;color:var(--cjs-accent,#a8323a)!important;text-decoration:none;}' +
      '.cjs-brand-page__faq{margin-top:48px!important;}' +
      '.cjs-brand-page__faq .cjs-brand-page__section-header{grid-template-columns:1fr!important;gap:8px!important;}' +
      '.cjs-brand-page__faq .cjs-brand-page__section-header h2{font-size:clamp(28px,3vw,40px)!important;}' +
      '.cjs-brand-page__faq-item summary{min-height:58px;padding:15px 22px;}' +
      '.cjs-brand-page__faq-answer{max-width:900px;padding:0 22px 18px;font-size:14px;line-height:1.6;}' +
      '.cjs-brand-page__faq-answer a{color:var(--cjs-accent);font-weight:700;text-decoration:underline;text-underline-offset:2px;}' +
      '@media(max-width:999px){.cjs-brand-performance-upgrades__grid{grid-template-columns:repeat(2,minmax(0,1fr));}}' +
      '@media(max-width:749px){.cjs-brand-page__trust-item{gap:14px;}.cjs-brand-page__trust-icon{width:42px!important;min-width:42px!important;height:42px!important;box-shadow:0 0 0 4px #f7f5f2,0 5px 12px rgba(17,17,17,.07)!important;}.cjs-brand-page__trust-icon svg{width:20px!important;height:20px!important;}.cjs-brand-performance-upgrades{margin-top:48px;padding-top:42px;}.cjs-brand-performance-upgrades__header{grid-template-columns:1fr;gap:12px;}.cjs-brand-performance-upgrades__grid{grid-template-columns:1fr;}.cjs-brand-performance-upgrades__card{padding:21px;}.cjs-brand-info-compact__card{padding:22px!important;}.cjs-brand-compact-actions{align-items:stretch;}.cjs-brand-compact-actions a{width:100%;}.cjs-brand-page__faq-item summary{min-height:54px;padding:14px 18px;}.cjs-brand-page__faq-answer{padding:0 18px 17px;}}';

    document.head.appendChild(style);
  }

  function run() {
    if (!brandPages[currentPath]) return;

    addStyles();
    loadFoxHeroImage();
    styleFoxHeroLogo();
    simplifyBrandPage();
    tidyServicesHeading();
    injectPerformanceUpgrades();
    simplifyInformation();
    tidyFaq();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }

  var observer = new MutationObserver(function () {
    if (!brandPages[currentPath]) return;

    observer.disconnect();
    run();
    observer.observe(document.documentElement, { childList: true, subtree: true });
  });

  if (brandPages[currentPath]) {
    observer.observe(document.documentElement, { childList: true, subtree: true });
  }
})();
